<template>
  <Teleport to="body">
    <div
      v-if="state.current"
      class="dlg-bg"
      @click.self="cancel"
      @keydown.esc="cancel"
    >
      <div class="dlg" @click.stop>
        <h3 v-if="opts.title" class="dlg-title">{{ opts.title }}</h3>
        <p v-if="opts.message" class="dlg-message">{{ opts.message }}</p>

        <template v-if="state.current.kind === 'prompt'">
          <label v-if="opts.label" class="dlg-label">{{ opts.label }}</label>
          <input
            ref="inputEl"
            v-model="inputValue"
            type="text"
            class="dlg-input"
            :placeholder="opts.placeholder || ''"
            @input="onMainInput"
            @keydown.enter="onConfirm"
            @keydown.esc="cancel"
          />

          <template v-if="opts.withSlug">
            <label class="dlg-label dlg-label-slug">{{ opts.withSlug.label || 'URL slug' }}</label>
            <input
              v-model="slugValue"
              type="text"
              class="dlg-input dlg-input-slug"
              :placeholder="opts.withSlug.placeholder || ''"
              @input="slugManuallyEdited = true"
              @keydown.enter="onConfirm"
              @keydown.esc="cancel"
            />
            <p v-if="opts.withSlug.hint" class="dlg-hint">{{ opts.withSlug.hint }}</p>
          </template>

          <p v-if="errorMessage" class="dlg-error">{{ errorMessage }}</p>
        </template>

        <div class="dlg-foot">
          <button
            type="button"
            class="dlg-btn ghost"
            @click="cancel"
          >{{ opts.cancelLabel || 'Cancel' }}</button>
          <button
            type="button"
            :class="['dlg-btn', opts.danger ? 'danger' : 'primary']"
            @click="onConfirm"
          >{{ opts.confirmLabel || 'Confirm' }}</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { _useDialogState, _resolveDialog } from '../../composables/useDialog.js';

const state = _useDialogState();
const opts = computed(() => state.current?.opts || {});

const inputValue = ref('');
const slugValue = ref('');
const slugManuallyEdited = ref(false);
const errorMessage = ref('');
const inputEl = ref(null);

watch(
  () => state.current,
  async (cur) => {
    if (!cur) return;
    if (cur.kind === 'prompt') {
      inputValue.value = cur.opts.defaultValue || '';
      slugManuallyEdited.value = false;
      slugValue.value = cur.opts.withSlug
        ? cur.opts.withSlug.fromValue?.(inputValue.value) || ''
        : '';
      errorMessage.value = '';
      await nextTick();
      inputEl.value?.focus();
      inputEl.value?.select();
    }
  }
);

function onMainInput() {
  if (state.current?.opts?.withSlug && !slugManuallyEdited.value) {
    slugValue.value = state.current.opts.withSlug.fromValue?.(inputValue.value) || '';
  }
}

function cancel() {
  _resolveDialog(state.current?.kind === 'prompt' ? null : false);
}

function onConfirm() {
  if (state.current?.kind === 'prompt') {
    const value = inputValue.value;
    const slug = state.current.opts.withSlug ? slugValue.value : undefined;
    const payload = state.current.opts.withSlug ? { value, slug } : value;
    const validate = state.current.opts.validate;
    if (typeof validate === 'function') {
      const err = validate(payload);
      if (err) {
        errorMessage.value = err;
        return;
      }
    }
    _resolveDialog(payload);
  } else {
    _resolveDialog(true);
  }
}
</script>

<style scoped>
.dlg-bg {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
  animation: dlg-fadein 0.15s ease-out;
}

@keyframes dlg-fadein {
  from { opacity: 0; }
  to { opacity: 1; }
}

.dlg {
  background: var(--cs-mono-white, #FFFFFF);
  border-radius: 10px;
  width: 440px;
  max-width: 92vw;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 32px rgba(23, 35, 61, 0.18);
  animation: dlg-rise 0.18s cubic-bezier(.2, 0, 0, 1);
  padding: 22px 24px 16px;
}

@keyframes dlg-rise {
  from { transform: translateY(8px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.dlg-title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: var(--cs-mono-ink-dark, #09121F);
}

.dlg-message {
  margin: 0 0 14px;
  font-size: 13px;
  color: var(--cs-mono-secondary, #6C6F78);
  line-height: 1.5;
}

.dlg-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: var(--cs-mono-primary, #313331);
  margin: 0 0 6px;
}

.dlg-input {
  appearance: none;
  width: 100%;
  border: 1px solid var(--cs-mono-divider, #D0DEE2);
  border-radius: 6px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
  color: var(--cs-mono-ink, #17233D);
  background: var(--cs-mono-white, #FFFFFF);
  outline: none;
}

.dlg-input:focus {
  border-color: var(--cs-brand-blue, #0077BD);
  box-shadow: 0 0 0 3px rgba(0, 119, 189, 0.18);
}

.dlg-error {
  margin: 6px 0 0;
  font-size: 12px;
  color: #B30022;
}

.dlg-label-slug {
  margin-top: 12px;
}

.dlg-input-slug {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  background: var(--cs-mono-bg, #F8F8F9);
}

.dlg-hint {
  margin: 6px 0 0;
  font-size: 11px;
  color: var(--cs-mono-caption, #808695);
}

.dlg-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 18px;
}

.dlg-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 14px;
  border-radius: 6px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
  color: var(--cs-mono-ink, #17233D);
}

.dlg-btn.ghost {
  color: var(--cs-mono-secondary, #6C6F78);
}

.dlg-btn.ghost:hover {
  background: var(--cs-mono-subtle-div, #E8EAEC);
  color: var(--cs-mono-ink, #17233D);
}

.dlg-btn.primary {
  background: var(--cs-brand-blue, #0077BD);
  color: white;
}

.dlg-btn.primary:hover {
  background: #0063A9;
}

.dlg-btn.danger {
  background: #D80027;
  color: white;
}

.dlg-btn.danger:hover {
  background: #B30022;
}
</style>
